import React, { useState } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import {
  Container,
  Title,
  Paper,
  Group,
  Button,
  Text,
  Breadcrumbs,
  Anchor,
  Alert,
  Tabs,
  MantineProvider,
} from '@mantine/core';
import { Notifications, showNotification } from '@mantine/notifications';
import { IconArrowLeft, IconInfoCircle, IconCalendar, IconAlertCircle } from '@tabler/icons-react';
import { QueryClient, QueryClientProvider, useMutation } from '@tanstack/react-query';
import { useDisclosure } from '@mantine/hooks';
import { LessonDetailsForm } from '../../components/PrivateLessons/LessonDetailsForm';
import { TimeSlotCalendar } from '../../components/PrivateLessons/TimeSlotCalendar';
import { TimeSlotDrawer } from '../../components/PrivateLessons/TimeSlotDrawer';
import axios from 'axios';

// Create a client for React Query
const queryClient = new QueryClient();

// Types
interface PrivateLessonPage {
  id: string;
  private_lesson_type_id: string;
  event_id: string;
  description: string;
  custom_location: string;
  additional_info: string;
  timezone: string;
  user: {
    username: string;
  };
  artist: {
    name: string;
    slug: string;
  };
  slug: string;
}

interface TimeSlot {
  id: string;
  start_time: string;
  end_time: string;
  available: boolean;
  status: 'available' | 'pending' | 'booked' | 'cancelled' | 'completed' | 'no-show';
  booking?: {
    id: string;
    status: string;
  };
}

// Setup axios with CSRF token
axios.defaults.headers.common['X-CSRF-TOKEN'] = document
  .querySelector('meta[name="csrf-token"]')
  ?.getAttribute('content');

// The main content component that doesn't use React Router hooks
const EditPrivateLessonPageContent = (props) => {
  // Get data from Inertia props
  const page = props;

  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string | null>('details');
  const [drawerType, setDrawerType] = useState<'create' | 'edit'>('create');
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<TimeSlot | null>(null);
  const [opened, { open, close }] = useDisclosure(false);
  const [isLoading, setIsLoading] = useState(false);

  // Update lesson page mutation
  // Update lesson page mutation
  const updateLessonPage = useMutation({
    mutationFn: async (updatedLessonPage: Partial<PrivateLessonPage>) => {
      setIsLoading(true);
      try {
        const response = await axios.patch(
          `/users/${page.user.username}/${page.artist.slug}/pl/${page.slug}`,
          updatedLessonPage,
        );
        return response.data;
      } finally {
        setIsLoading(false);
      }
    },
    onSuccess: (data) => {
      // Show success notification
      showNotification({
        title: 'Success',
        message: data.message || 'Private lesson page was successfully updated.',
        color: 'green',
        autoClose: 3000,
        position: 'top-right',
      });

      // Navigate to the page after showing the notification
      setTimeout(() => {
        window.location.href = data.redirect_url || `/${page.artist.slug}/pl/${page.slug}`;
      }, 1000);
    },
    onError: (error: any) => {
      // Extract error messages
      let errorMessage = 'Failed to update lesson page. Please try again.';
      if (error.response && error.response.data && error.response.data.message) {
        errorMessage = error.response.data.message;
      }

      // Show error notification
      showNotification({
        title: 'Error',
        message: errorMessage,
        color: 'red',
        autoClose: 5000,
      });

      setError(errorMessage);
      console.error('Update error:', error);
    },
  });

  // Create time slots mutation
  const createTimeSlots = useMutation({
    mutationFn: async (timeSlotsData: any) => {
      setIsLoading(true);
      try {
        const response = await axios.post(
          `/users/${page.user.username}/${page.artist.slug}/pl/${page.slug}/time_slots/batch`,
          { time_slots: timeSlotsData },
        );
        return response.data;
      } finally {
        setIsLoading(false);
      }
    },
    onSuccess: () => {
      // Refresh the page to show updated time slots
      window.location.reload();
      close();
    },
    onError: (error) => {
      setError('Failed to create time slots. Please try again.');
      console.error('Create time slots error:', error);
    },
  });

  // Delete time slot mutation
  const deleteTimeSlot = useMutation({
    mutationFn: async (timeSlotId: string) => {
      setIsLoading(true);
      try {
        const response = await axios.delete(
          `/users/${page.user.username}/${page.artist.slug}/pl/${page.slug}/time_slots/${timeSlotId}`,
        );
        return response.data;
      } finally {
        setIsLoading(false);
      }
    },
    onSuccess: () => {
      // Refresh the page to show updated time slots
      window.location.reload();
    },
    onError: (error) => {
      setError('Failed to delete time slot. Please try again.');
      console.error('Delete time slot error:', error);
    },
  });

  // Update time slot mutation
  const updateTimeSlot = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<TimeSlot> }) => {
      setIsLoading(true);
      try {
        const response = await axios.patch(
          `/users/${page.user.username}/${page.artist.slug}/pl/${page.slug}/time_slots/${id}`,
          data,
        );
        return response.data;
      } finally {
        setIsLoading(false);
      }
    },
    onSuccess: () => {
      // Refresh the page to show updated time slots
      window.location.reload();
      close();
    },
    onError: (error) => {
      setError('Failed to update time slot. Please try again.');
      console.error('Update time slot error:', error);
    },
  });

  // Handler functions
  const handleGoBack = () => {
    window.history.back();
  };

  // Handle creating time slots
  const handleCreateTimeSlots = (timeSlotData: any) => {
    createTimeSlots.mutate(timeSlotData);
  };

  // Handle editing a time slot
  const handleEditTimeSlot = (timeSlot: TimeSlot) => {
    setSelectedTimeSlot(timeSlot);
    setDrawerType('edit');
    open();
  };

  // Handle deleting a time slot
  const handleDeleteTimeSlot = (timeSlotId: string) => {
    if (confirm('Are you sure you want to delete this time slot?')) {
      deleteTimeSlot.mutate(timeSlotId);
    }
  };

  // Open drawer for creating new time slots
  const handleOpenCreateDrawer = () => {
    setSelectedTimeSlot(null);
    setDrawerType('create');
    open();
  };

  // Handle updating a time slot
  const handleUpdateTimeSlot = (id: string, data: Partial<TimeSlot>) => {
    updateTimeSlot.mutate({ id, data });
  };

  return (
    <MantineProvider>
      <Notifications />
      <Container size="lg" py="xl" style={{ backgroundColor: '#424242', minHeight: '100vh' }}>
        {error && (
          <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red" mb="md">
            {error}
          </Alert>
        )}

        {/* Page title */}
        <Title order={1} mb="xl" className="text-white poppins">
          Edit Private Lesson Page - {page.description}
        </Title>

        {/* Main content with tabs */}
        <Paper
          p="sm"
          shadow="sm"
          style={{
            backgroundColor: '#757575',
            textAlign: 'center',
            borderRadius: '10px',
          }}
        >
          <Tabs color="orange" value={activeTab} onChange={setActiveTab}>
            <Tabs.List>
              <Tabs.Tab value="details" leftSection={<IconInfoCircle size={16} />}>
                Lesson Details
              </Tabs.Tab>
              <Tabs.Tab value="timeSlots" leftSection={<IconCalendar size={16} />}>
                Time Slots
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="details" pt="md">
              <LessonDetailsForm
                lessonPage={page}
                onSubmit={(data) => updateLessonPage.mutate(data)}
                isLoading={isLoading}
                availableLessonTypes={page.available_lesson_types || []}
                availableEvents={page.available_events || []}
              />
            </Tabs.Panel>

            <Tabs.Panel value="timeSlots" pt="md">
              <TimeSlotCalendar
                timeSlots={page.time_slots || []}
                isLoading={isLoading}
                onAddTimeSlots={handleOpenCreateDrawer}
                onEditTimeSlot={handleEditTimeSlot}
                onDeleteTimeSlot={handleDeleteTimeSlot}
                onUpdateStatus={(id, status) => handleUpdateTimeSlot(id, { status })}
              />
            </Tabs.Panel>
          </Tabs>
        </Paper>

        {/* Time Slot Drawer */}
        <TimeSlotDrawer
          opened={opened}
          onClose={close}
          type={drawerType}
          timeSlot={selectedTimeSlot}
          onCreateTimeSlots={handleCreateTimeSlots}
          onUpdateTimeSlot={(id, data) => handleUpdateTimeSlot(id, data)}
          isSubmitting={isLoading}
          timezone={page.timezone}
        />
      </Container>
    </MantineProvider>
  );
};

// Main component that wraps the content with providers
const EditPrivateLessonPage = (props) => {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <EditPrivateLessonPageContent {...props} />
      </Router>
    </QueryClientProvider>
  );
};

export default EditPrivateLessonPage;
